import { useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sheet from '../ui/Sheet';
import { LAYERS } from '../ui/layers';
import ChatHub from './ChatHub';
import useAddressSheet from '../../hooks/useAddressSheet';
import { can } from '../../utils/permissions';
import * as S from '../specimens/styles';
import { CHAT_INBOX, chatTarget } from './openChat';

export { CHAT_INBOX, ChatDrawerContext, chatTarget } from './openChat';

/*
 * The global chat drawer (plan phase 8; docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 5; docs/CHAT.md). The header's chat button opens the conversations
 * from the left, over whatever page is open, and the page stays as it was
 * underneath. It is a ui/Sheet -- a Dialog: focus in and trapped, Escape, the
 * x, the dimmed page and the grip close it -- around ChatHub in one column,
 * the same hub as the /chat page and the Field camera's messages sheet. Being
 * a modal sheet, it stops the page swipe while it is open (ui/useModal.js
 * anyModalOpen).
 *
 * It lives in the address (hooks/useAddressSheet.js): ?chat=inbox is the list,
 * ?chat=<channel id> a conversation. Opening pushes one history entry; moving
 * between the list and a conversation replaces it, so the phone's Back closes
 * the drawer and leaves the page where it was, and a link or a reload opens
 * it again. "Open full page" goes to /chat?c=<channel> in place of the
 * drawer's entry, so Back from there returns to the page beneath.
 *
 * Not on /chat itself (the page is the same hub), not in Field Mode (a bare
 * route with no Layout, which keeps its own messages sheet), and nothing at
 * all for an account without chat:read (the public survey's). The read-only
 * executive reads and marks read; ChatHub offers writing only with chat:use.
 *
 * A page opens a conversation in it through ./openChat.js (useOpenChat), from
 * the opener Layout puts in ChatDrawerContext: the handoffs' Message buttons,
 * one of them inside a handoff's dialog. So the drawer floats above a page's
 * dialogs and confirmations (LAYERS.chat), and Back returns to that dialog.
 */
export const CHAT_PARAM = 'chat';
const STATE_KEY = 'chatSheetPushed';
const NOTHING = Object.freeze([]);

const onChatPage = (pathname) => pathname === '/chat' || pathname.startsWith('/chat/');

/**
 * The drawer's place in the address, for the host (Layout) and its button:
 *
 *   const chat = useChatDrawer(user, { returnFocus: () => chatButtonRef.current });
 *   <ChatButton onChat={chat.offered ? chat.show : undefined} />
 *   <ChatDrawer user={user} chat={chat} />
 *
 * `value` is what is open (null, 'inbox' or a channel id), `channel` the open
 * conversation ('' for the list). `show(target)` opens the list or a
 * conversation, `close()` goes back through the drawer's entry.
 *
 * Focus goes back to whatever opened the drawer (useModal). When that is gone
 * -- a phone's menu closes behind its chat button -- `returnFocus()` names
 * where it goes instead.
 */
export function useChatDrawer(user, { returnFocus } = {}) {
  const { pathname } = useLocation();
  const offered = can(user, 'chat:read') && !onChatPage(pathname);
  const { sheet, open, close } = useAddressSheet(CHAT_PARAM, offered ? chatTarget : NOTHING, { stateKey: STATE_KEY });
  const show = useCallback((target) => open(target || CHAT_INBOX), [open]);

  const returnTo = useRef(returnFocus);
  returnTo.current = returnFocus;
  const wasOpen = useRef(false);
  useEffect(() => {
    if (sheet) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    // The sheet's own clean-up has run by now: only if it found nothing to
    // give focus back to does the host's fallback take it.
    const active = document.activeElement;
    if (active && active !== document.body) return;
    returnTo.current?.()?.focus?.({ preventScroll: true });
  }, [sheet]);

  return { value: sheet, channel: sheet && sheet !== CHAT_INBOX ? sheet : '', offered, show, close };
}

export default function ChatDrawer({ user, chat }) {
  const { t } = useTranslation(['chat', 'common']);
  const { state } = useLocation();
  if (!chat?.value || !can(user, 'chat:read')) return null;

  const { channel } = chat;
  // In place of the drawer's own entry, when it pushed one: Back from the
  // full page then returns to the page beneath, not to the drawer.
  const pushed = !!(state && typeof state === 'object' && state[STATE_KEY]);
  const fullPage = channel ? `/chat?c=${encodeURIComponent(channel)}` : '/chat';

  return (
    <Sheet side="left" title={t('chat:title')} onClose={chat.close} layer={LAYERS.chat} testId="chat-drawer"
      actions={(
        <Link to={fullPage} replace={pushed} title={t('chat:drawer.fullPageTip')} data-testid="chat-drawer-full"
          style={fullPageStyle}>
          {t('chat:drawer.fullPage')}
        </Link>
      )}>
      <ChatHub user={user} selected={channel} onSelect={(id) => chat.show(id || CHAT_INBOX)} narrow
        listHeadingAs="h3" testId="chat-drawer-hub" style={{ height: '100%' }} />
    </Sheet>
  );
}

const fullPageStyle = {
  ...S.ghostBtn, fontSize: 13, padding: '6px 10px', textDecoration: 'none', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center',
};
